import React from 'react';
import { DraftRestoreController } from '../contexts/DraftContext';

/**
 * Renders the restore UI for a screen's draft controller (useDraftRestore):
 *   - 'restored' → a dismissible banner, naming any sensitive fields.
 *   - 'conflict' → a blocking choice (the record changed server-side); never
 *     auto-applies, never silently discards.
 *   - 'none'     → nothing.
 */
const fmtTime = (ts: number | null) => {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

export const DraftNotice: React.FC<{ controller: DraftRestoreController }> = ({ controller }) => {
  const { mode, createdAt, changedFields, sensitiveLabels, dismiss, keepMine, discard } = controller;

  if (mode === 'restored') {
    return (
      <div role="status" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        margin: '8px 0', padding: '8px 12px', borderRadius: 8,
        background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', fontSize: 13,
      }}>
        <span>
          Restored your unsaved changes{createdAt ? ` from ${fmtTime(createdAt)}` : ''}.
          {sensitiveLabels.length > 0 && (
            <strong> Includes changes to: {sensitiveLabels.join(', ')}.</strong>
          )}
        </span>
        <button onClick={discard}
          style={{ background: 'none', border: '1px solid #93c5fd', color: '#1e40af', borderRadius: 6, padding: '2px 10px', cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}>
          Discard
        </button>
        <button onClick={dismiss} aria-label="Dismiss"
          style={{ background: 'none', border: 'none', color: '#1e40af', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>
          ×
        </button>
      </div>
    );
  }

  if (mode === 'conflict') {
    return (
      <div role="alertdialog" aria-modal="true" style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}>
        <div style={{ background: '#fff', borderRadius: 10, maxWidth: 460, width: '100%', padding: 20, boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
          <div style={{ fontWeight: 700, color: '#991b1b', fontSize: 16, marginBottom: 6 }}>
            This record was changed by someone else while you were away.
          </div>
          <p style={{ color: '#374151', fontSize: 14, marginTop: 0 }}>
            You have unsaved changes to this record, but its current version on the server is
            different from when you started editing.
          </p>
          {changedFields.length > 0 && (
            <p style={{ color: '#6b7280', fontSize: 13 }}>
              Your changes would affect: {changedFields.join(', ')}.
              {sensitiveLabels.length > 0 && (
                <strong style={{ color: '#92400e' }}> Including sensitive fields: {sensitiveLabels.join(', ')}.</strong>
              )}
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
            <button onClick={keepMine}
              style={{ background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontWeight: 600 }}>
              Use my unsaved changes
            </button>
            <button onClick={discard}
              style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 14px', cursor: 'pointer' }}>
              Discard mine and use the current version
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default DraftNotice;
